import {
  createContext,
  useContext,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react'
import { EVENT_NAMES } from 'services/Analytics/Analytics'
import { getCartUpsellStatuses } from 'features/cartUpsells/cartUpsellsStorage'
import { type BagOpenSource } from '../cart.consts'
import { useBagAnalytics } from '../hooks/useBagAnalytics'

type CartDrawerContextValue = {
  isCartOpen: boolean
  openCart: (source: BagOpenSource) => void
  closeCart: () => void
}

const CartDrawerContext = createContext<CartDrawerContextValue | null>(null)
CartDrawerContext.displayName = 'CartDrawerContext'

export function CartDrawerProvider({ children }: PropsWithChildren) {
  const [isCartOpen, setIsCartOpen] = useState(false)
  const { trackBagEvent } = useBagAnalytics()

  const value = useMemo<CartDrawerContextValue>(
    () => ({
      isCartOpen,
      openCart: (source) => {
        trackBagEvent(EVENT_NAMES.CART_BAG_OPENED, {
          'Bag Entry Point': source,
          'Cart Upsell Statuses': getCartUpsellStatuses(),
        })
        setIsCartOpen(true)
      },
      closeCart: () => setIsCartOpen(false),
    }),
    [isCartOpen, trackBagEvent]
  )

  return (
    <CartDrawerContext.Provider value={value}>
      {children}
    </CartDrawerContext.Provider>
  )
}

export function useCartDrawer(): CartDrawerContextValue {
  const context = useContext(CartDrawerContext)
  if (!context) {
    throw new Error('useCartDrawer must be used within a CartDrawerProvider')
  }
  return context
}
